/**
 * Seamless Pee Safe announcement marquee.
 * Duplicates messages until the track fills the viewport, then loops with no gap or pause.
 * Cloning + animation start on window load so LCP/INP stay clear of main-thread work.
 */
class PeeSafeAnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this.#viewport = this.querySelector('[data-pee-safe-marquee-viewport]');
    this.#track = this.querySelector('[data-pee-safe-marquee-track]');

    if (!this.#viewport || !this.#track) return;

    this.#originalCount = this.#getOriginalItems().length;
    if (this.#originalCount <= 0) return;

    this.#scheduleStart();
  }

  disconnectedCallback() {
    this.#teardownLoadListener();
    this.#resizeObserver?.disconnect();
    if (this.#updateFrame) cancelAnimationFrame(this.#updateFrame);
    if (this.#startTimer) clearTimeout(this.#startTimer);
  }

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {number} */
  #originalCount = 0;

  /** @type {boolean} */
  #started = false;

  /** @type {(() => void) | null} */
  #onWindowLoad = null;

  /** @type {number | undefined} */
  #startTimer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {number | undefined} */
  #updateFrame;

  #shouldStartImmediately() {
    return !!(window.Shopify && window.Shopify.designMode) || document.readyState === 'complete';
  }

  #scheduleStart() {
    if (this.#shouldStartImmediately()) {
      this.#start();
      return;
    }

    this.#onWindowLoad = () => this.#start();
    window.addEventListener('load', this.#onWindowLoad, { once: true });
  }

  #teardownLoadListener() {
    if (!this.#onWindowLoad) return;
    window.removeEventListener('load', this.#onWindowLoad);
    this.#onWindowLoad = null;
  }

  #start() {
    if (this.#started || !this.isConnected) return;
    this.#started = true;
    this.#teardownLoadListener();

    // Yield so load handlers and paint settle before cloning/animation (LCP / INP).
    this.#startTimer = window.setTimeout(() => {
      this.#startTimer = undefined;
      this.#initMarquee();
    }, 0);
  }

  #initMarquee() {
    if (!this.isConnected || !this.#viewport || !this.#track) return;

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleUpdate());
    this.#resizeObserver.observe(this.#viewport);
    this.#resizeObserver.observe(this.#track);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.#update();
      });
    }

    this.#update();
  }

  #scheduleUpdate() {
    if (this.#updateFrame) cancelAnimationFrame(this.#updateFrame);
    this.#updateFrame = requestAnimationFrame(() => {
      this.#updateFrame = undefined;
      this.#update();
    });
  }

  #getOriginalItems() {
    if (!this.#track) return [];
    return [...this.#track.querySelectorAll('[data-pee-safe-marquee-item]:not([data-pee-safe-marquee-clone])')];
  }

  #appendCopySet() {
    const originals = this.#getOriginalItems();
    originals.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('data-pee-safe-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-pee-safe-marquee-item');
      this.#track?.appendChild(clone);
    });
  }

  #getCopySetCount() {
    if (!this.#track || this.#originalCount <= 0) return 0;
    return Math.floor(this.#track.children.length / this.#originalCount);
  }

  #update() {
    if (!this.#track || !this.#viewport) return;

    let copySets = this.#getCopySetCount();

    if (copySets < 2) {
      this.#appendCopySet();
      copySets = this.#getCopySetCount();
    }

    const viewportWidth = this.#viewport.clientWidth;
    let halfWidth = this.#track.scrollWidth / 2;

    while (halfWidth < viewportWidth && copySets < 20) {
      this.#appendCopySet();
      copySets = this.#getCopySetCount();
      halfWidth = this.#track.scrollWidth / 2;
    }

    if (copySets % 2 !== 0) {
      this.#appendCopySet();
    }

    halfWidth = this.#track.scrollWidth / 2;
    if (halfWidth <= 0) return;

    const speed = parseFloat(this.getAttribute('speed') || '20');

    this.#track.style.setProperty('--pee-safe-marquee-shift', `${halfWidth}px`);
    this.#track.style.setProperty('--pee-safe-marquee-duration', `${Math.max(8, speed)}s`);
    this.#track.classList.add('pesafanouceanimation');
  }
}

if (!customElements.get('pee-safe-announcement-marquee')) {
  customElements.define('pee-safe-announcement-marquee', PeeSafeAnnouncementMarquee);
}
